import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsCarBooking';

const TITLETAB = 'Asignación de Permisos de Aprobación';
const endpointAprobadores = ENDPOINTS.aprobadores;
const endpointAprobadoresById = ENDPOINTS.aprobadoresById;

// Catálogos
const endpointEmpleado = ENDPOINTS.empleados;
const modeloEmpleado = Models.empleados;
const endpointPersona = ENDPOINTS.personas;
const modeloPersona = Models.persona;
const endpointTipoAprobacion = ENDPOINTS.tipoAprobacion;
const modeloTipoAprobacion = Models.tipoAprobacion;

export default function Modal({
  open,
  mode = 'new',
  data = null,
  onClose,
  onSubmit, // Se usará para notificar al padre que recargue
  inline = false,
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Selección de empleado
  const [selectedIdEmpleado, setSelectedIdEmpleado] = useState(0);
  const [empleadosOptions, setEmpleadosOptions] = useState([]);

  // Drag & Drop State
  const [allTipos, setAllTipos] = useState([]);
  const [initialAssigned, setInitialAssigned] = useState([]);
  const [currentAssigned, setCurrentAssigned] = useState([]);
  const [dragOverColumn, setDragOverColumn] = useState(null);

  // Cargar catálogos iniciales (Empleados y Tipos)
  useEffect(() => {
    if (!open) return;

    const loadCatalogs = async () => {
      setLoading(true);
      try {
        const [resEmps, resPers, resTipos] = await Promise.all([
          axios.get(endpointEmpleado),
          axios.get(endpointPersona),
          axios.get(endpointTipoAprobacion),
        ]);

        const emps = (resEmps.data || []).map(modeloEmpleado);
        const pers = (resPers.data || []).map(modeloPersona);
        const tipos = (resTipos.data || []).map(modeloTipoAprobacion);

        // Enriquecer empleados
        const empsEnriched = emps.map(emp => {
          const per = pers.find(p => p.idPersona === emp.idPersona);
          const fullName = per
            ? `${per.nombre || ''} ${per.apellidoPaterno || ''} ${per.apellidoMaterno || ''}`.trim()
            : `Empleado #${emp.idEmpleado}`;
          return { ...emp, _nombreUI: fullName };
        });

        setEmpleadosOptions(empsEnriched);
        setAllTipos(tipos);

      } catch (e) {
        console.error('Error cargando catálogos', e);
      } finally {
        setLoading(false);
      }
    };

    loadCatalogs();
  }, [open]);

  // Inicializar estado según modo
  useEffect(() => {
    if (!open) return;

    if (mode === 'edit' && data) {
      setSelectedIdEmpleado(data.idEmpleado || 0);
    } else {
      setSelectedIdEmpleado(0);
      setInitialAssigned([]);
      setCurrentAssigned([]);
    }
  }, [open, mode, data]);

  // Cargar permisos del empleado seleccionado
  useEffect(() => {
    if (!selectedIdEmpleado) {
      setInitialAssigned([]);
      setCurrentAssigned([]);
      return;
    }

    const loadPermisos = async () => {
      setLoading(true);
      try {
        // Traemos TODOS los aprobadores y filtramos (API no tiene byEmpleado?)
        const res = await axios.get(endpointAprobadores);
        const todos = res.data || [];

        const misPermisos = todos.filter(x => (x.idEmpleado || x.IdEmpleado) === selectedIdEmpleado);

        // Mapear a estructura interna soportando PascalCase
        const mapped = misPermisos.map(p => ({
          idAprobadores: p.idAprobadores ?? p.IdAprobadores ?? 0,
          idEmpleado: p.idEmpleado ?? p.IdEmpleado ?? 0,
          idTipoAprobacion: p.idTipoAprobacion ?? p.IdTipoAprobacion ?? 0,
          activo: p.activo ?? p.Activo ?? false
        }));

        setInitialAssigned(mapped);
        setCurrentAssigned(mapped);

      } catch (e) {
        console.error('Error cargando permisos del empleado', e);
      } finally {
        setLoading(false);
      }
    };

    loadPermisos();
  }, [selectedIdEmpleado]);


  // --- Cálculos D&D ---

  const availableTipos = useMemo(() => {
    const assignedIds = new Set(currentAssigned.map(r => r.idTipoAprobacion));
    return allTipos.filter(t => !assignedIds.has(t.idTipoAprobacion));
  }, [allTipos, currentAssigned]);

  const enrichedAssigned = useMemo(() => {
    return currentAssigned.map(item => {
      const tipo = allTipos.find(t => t.idTipoAprobacion === item.idTipoAprobacion);
      return {
        ...item,
        nombreTipo: tipo?.nombre || `Tipo #${item.idTipoAprobacion}`,
        descripcion: tipo?.descripcion
      };
    });
  }, [currentAssigned, allTipos]);

  // --- Handlers D&D ---

  const handleDragStart = (e, item, source) => {
    e.dataTransfer.setData('application/json', JSON.stringify({ item, source }));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, targetColumn) => {
    e.preventDefault();
    if (dragOverColumn !== targetColumn) {
      setDragOverColumn(targetColumn);
    }
  };

  const handleDrop = (e, targetColumn) => {
    e.preventDefault();
    setDragOverColumn(null);

    const dataStr = e.dataTransfer.getData('application/json');
    if (!dataStr) return;

    const { item, source } = JSON.parse(dataStr);
    if (source === targetColumn) return;

    if (targetColumn === 'assigned' && source === 'available') {
      addLocal(item); // item es un TipoAprobacion
    } else if (targetColumn === 'available' && source === 'assigned') {
      removeLocal(item); // item es un Aprobador (relación)
    }
  };

  const addLocal = (tipoItem) => {
    const exists = currentAssigned.some(r => r.idTipoAprobacion === tipoItem.idTipoAprobacion);
    if (exists) return;

    const newAssignment = {
      idAprobadores: 0, // Nuevo
      idEmpleado: selectedIdEmpleado,
      idTipoAprobacion: tipoItem.idTipoAprobacion,
      activo: true
    };
    setCurrentAssigned(prev => [...prev, newAssignment]);
  };

  const removeLocal = (assignedItem) => {
    setCurrentAssigned(prev => prev.filter(r => r.idTipoAprobacion !== assignedItem.idTipoAprobacion));
  };

  // --- Guardar ---

  const handleSave = async () => {
    if (!selectedIdEmpleado) {
      alert('Selecciona un empleado primero.');
      return;
    }

    setSaving(true);
    try {
      // 1. Identificar agregados
      const initialIds = new Set(initialAssigned.map(r => r.idTipoAprobacion));
      const toAdd = currentAssigned.filter(r => !initialIds.has(r.idTipoAprobacion));

      // 2. Identificar removidos
      const currentIds = new Set(currentAssigned.map(r => r.idTipoAprobacion));
      const toRemove = initialAssigned.filter(r => !currentIds.has(r.idTipoAprobacion));

      const promises = [];

      // Add
      for (const item of toAdd) {
        const payload = {
          idEmpleado: selectedIdEmpleado,
          idTipoAprobacion: item.idTipoAprobacion,
          activo: true
        };
        promises.push(axios.post(endpointAprobadores, payload));
      }

      // Remove
      for (const item of toRemove) {
        if (item.idAprobadores > 0) {
          promises.push(axios.delete(endpointAprobadoresById(item.idAprobadores)));
        }
      }

      await Promise.all(promises);

      // Notificar al padre (pasamos null o algo dummy, lo importante es que recargue)
      onSubmit({}, 'refresh');
      onClose();

    } catch (e) {
      console.error(e);
      alert('Error al guardar cambios.');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  // --- Render ---

  const Header = (
    <div className="roles-topbar" style={{ marginBottom: '0.6rem', paddingRight: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: '0 0 auto' }}>
      <div>
        <h3 className="roles-title" style={{ margin: 0 }}>{TITLETAB}</h3>
        <div style={{ fontSize: '0.9rem', color: '#666' }}>
          {mode === 'new' ? 'Selecciona un empleado y asigna permisos.' : 'Modifica los permisos del empleado.'}
        </div>
      </div>
      <button className="roles-btn" onClick={onClose} aria-label="Cerrar">✕</button>
    </div>
  );

  const Body = (
    <div style={{ paddingLeft: '2%', paddingRight: '2%', display: 'flex', flexDirection: 'column', gap: '1rem', overflowY: 'auto', flex: '1 1 auto', minHeight: 0 }}>

      {/* Selector de Empleado */}
      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Empleado:</label>
        <br /><br />
        <select
          className="infoField"
          value={selectedIdEmpleado}
          onChange={(e) => setSelectedIdEmpleado(Number(e.target.value))}
          disabled={mode === 'edit'} // En edit no se cambia el empleado
          style={{ maxWidth: '50%' }}
        >
          <option value={0}>— Selecciona un empleado —</option>
          {empleadosOptions.map(emp => (
            <option key={emp.idEmpleado} value={emp.idEmpleado}>
              {emp._nombreUI}
            </option>
          ))}
        </select>
      </div>

      {/* Drag & Drop Container */}
      {selectedIdEmpleado > 0 && (
        <div className="dnd-container" style={{ flex: 1, minHeight: '300px' }}>
          {/* Left: Available */}
          <div
            className={`dnd-column ${dragOverColumn === 'available' ? 'drag-over' : ''}`}
            onDragOver={(e) => handleDragOver(e, 'available')}
            onDrop={(e) => handleDrop(e, 'available')}
          >
            <div className="dnd-header">Permisos Disponibles</div>
            {loading ? (
              <div className="dnd-empty">Cargando...</div>
            ) : availableTipos.length === 0 ? (
              <div className="dnd-empty">No hay más permisos disponibles</div>
            ) : (
              availableTipos.map(tipo => (
                <div
                  key={tipo.idTipoAprobacion}
                  className="dnd-item"
                  draggable={!saving}
                  onDragStart={(e) => handleDragStart(e, tipo, 'available')}
                >
                  <span>{tipo.nombre}</span>
                  <button
                    type="button"
                    onClick={() => addLocal(tipo)}
                    style={{ background: 'none', border: 'none', fontSize: '1.2em', color: '#28a745', cursor: 'pointer', padding: '0 5px' }}
                  >＋</button>
                </div>
              ))
            )}
          </div>

          {/* Right: Assigned */}
          <div
            className={`dnd-column ${dragOverColumn === 'assigned' ? 'drag-over' : ''}`}
            onDragOver={(e) => handleDragOver(e, 'assigned')}
            onDrop={(e) => handleDrop(e, 'assigned')}
          >
            <div className="dnd-header">Permisos Asignados</div>
            {loading ? (
              <div className="dnd-empty">Cargando...</div>
            ) : enrichedAssigned.length === 0 ? (
              <div className="dnd-empty">Arrastra permisos aquí</div>
            ) : (
              enrichedAssigned.map(item => (
                <div
                  key={item.idTipoAprobacion}
                  className="dnd-item"
                  draggable={!saving}
                  onDragStart={(e) => handleDragStart(e, item, 'assigned')}
                >
                  <span>{item.nombreTipo}</span>
                  <button
                    type="button"
                    onClick={() => removeLocal(item)}
                    style={{ background: 'none', border: 'none', fontSize: '1.2em', color: '#dc3545', cursor: 'pointer', padding: '0 5px' }}
                  >✕</button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );

  const Footer = (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '.6rem', marginTop: '.8rem', paddingRight: 16, flex: '0 0 auto' }}>
      <button className="roles-btn" onClick={onClose}>Cancelar</button>
      <button
        className="btn-action"
        onClick={handleSave}
        disabled={saving || !selectedIdEmpleado}
        style={{ opacity: (saving || !selectedIdEmpleado) ? 0.5 : 1 }}
      >
        {saving ? 'Guardando...' : 'Guardar Cambios'}
      </button>
    </div>
  );

  if (inline) {
    return (
      <div className="roles-inline-card" style={{ display: 'flex', flexDirection: 'column', gap: 0, height: '100%' }}>
        {Header}
        {Body}
        {Footer}
      </div>
    );
  }

  return (
    <div className="roles-confirm-overlay" role="dialog" aria-modal="true"
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 1000 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="roles-confirm-card"
        style={{ width: 'min(900px, 96vw)', height: '85vh', background: '#fff', borderRadius: 12, boxShadow: '0 10px 30px rgba(0,0,0,.25)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        {Header}
        {Body}
        {Footer}
      </div>
    </div>
  );
}
